import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { FiBell } from 'react-icons/fi';
import { notificationsAPI } from '../services/api';

const formatTimestamp = (value) => {
  if (!value) return '';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMinutes = Math.floor(diffMs / 60000);

  if (diffMinutes < 1) return 'Just now';
  if (diffMinutes < 60) return `${diffMinutes}m ago`;

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}h ago`;

  return date.toLocaleDateString();
};

const NotificationBell = ({ compact = false }) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const queryClient = useQueryClient();

  const notificationsQuery = useQuery(
    ['notifications'],
    () => notificationsAPI.getNotifications({ limit: 10 }),
    {
      refetchInterval: 60000,
      staleTime: 15000
    }
  );

  const markAsReadMutation = useMutation(
    (id) => notificationsAPI.markAsRead(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['notifications']);
      }
    }
  );

  const markAllAsReadMutation = useMutation(
    () => notificationsAPI.markAllAsRead(),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['notifications']);
      }
    }
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const notifications = notificationsQuery.data?.data || [];
  const unreadCount = notificationsQuery.data?.unreadCount || 0;

  const buttonClassName = useMemo(() => (
    compact
      ? 'relative p-2 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 rounded-full'
      : 'bg-white p-1 rounded-full text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
  ), [compact]);

  const handleNotificationClick = async (notification) => {
    if (!notification.readAt) {
      await markAsReadMutation.mutateAsync(notification.id);
    }

    setOpen(false);

    if (notification.actionUrl) {
      window.location.href = notification.actionUrl;
    }
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        className={buttonClassName}
        onClick={() => setOpen((current) => !current)}
        title="Notifications"
      >
        <span className="sr-only">View notifications</span>
        <FiBell className={compact ? 'h-5 w-5' : 'h-6 w-6'} />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[1.1rem] rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-semibold leading-none text-white">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 rounded-lg border border-gray-200 bg-white shadow-lg z-50">
          <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
            <div>
              <h3 className="text-sm font-semibold text-gray-900">Notifications</h3>
              <p className="text-xs text-gray-500">{unreadCount} unread</p>
            </div>
            <button
              type="button"
              onClick={() => markAllAsReadMutation.mutate()}
              disabled={unreadCount === 0 || markAllAsReadMutation.isLoading}
              className="text-xs font-medium text-indigo-600 hover:text-indigo-700 disabled:text-gray-400"
            >
              Mark all read
            </button>
          </div>

          <div className="max-h-96 overflow-y-auto">
            {notificationsQuery.isLoading && (
              <div className="px-4 py-6 text-sm text-gray-500">Loading notifications...</div>
            )}

            {!notificationsQuery.isLoading && notifications.length === 0 && (
              <div className="px-4 py-6 text-sm text-gray-500">No notifications yet.</div>
            )}

            {notifications.map((notification) => (
              <button
                type="button"
                key={notification.id}
                onClick={() => handleNotificationClick(notification)}
                className={`w-full border-b border-gray-100 px-4 py-3 text-left hover:bg-gray-50 ${notification.readAt ? 'bg-white' : 'bg-indigo-50/40'}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{notification.title}</p>
                    <p className="mt-1 text-sm text-gray-600">{notification.message}</p>
                  </div>
                  {!notification.readAt && (
                    <span className="mt-1 h-2.5 w-2.5 rounded-full bg-indigo-500 flex-shrink-0" />
                  )}
                </div>
                <p className="mt-2 text-xs text-gray-400">{formatTimestamp(notification.createdAt || notification.created_at)}</p>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
